<template>
  <!-- Pass timer component - displays information and control buttons based on pass status -->
  <div
    class="active-pass-timer bg-white rounded-30 d-flex align-items-center justify-content-center w-100"
    :class="{ [timerStatusClass]: true, shadow: !isInQueue }"
  >
    <!-- Information display for queue -->
    <template v-if="isInQueue">
      <!-- Queue position information -->
      <div class="queue-info-container">
        <div class="queue-divider" />

        <div class="queue-position-container">
          <span class="queue-label">{{ $t("common.you_are") }}</span>
          <span class="queue-position-value">
            #{{ activePassQueuePosition }}
          </span>
        </div>
      </div>
    </template>

    <!-- Information display when not in queue -->
    <template v-else>
      <!-- Waiting hourglass icon -->
      <span
        v-if="isWaiting && !isValidAutoPass"
        class="flaticon-hour-glass-2-optimized"
      >
        <span class="path1" />
        <span class="path2" />
        <span class="path3" />
        <span class="path4" />
        <span class="path5" />
        <span class="path6" />
        <span class="path7" />
        <span class="path8" />
        <span class="path9" />
        <span class="path10" />
      </span>

      <!-- Start button for auto pass -->
      <div
        v-if="showStartButton"
        class="pt-3 text-green d-flex flex-column align-items-center"
      >
        <i class="flaticon-play-circle" @click="$emit('auto-approve')" />
        <span class="font-sm">{{ $t("common.start") }}</span>
      </div>

      <!-- Check icon for approved passes -->
      <i
        v-if="showCheckIcon"
        class="fi flaticon-check me-3"
        @click="handleCheckAction"
      />

      <!-- Stop button for active passes -->
      <div
        v-if="showStopButton"
        class="pt-3 d-flex flex-column align-items-center text-red"
      >
        <i class="flaticon-stop-circle" @click="$emit('auto-pause')" />
        <span class="font-sm">{{ $t("common.stop") }}</span>
      </div>

      <!-- Finish line icon for expired passes -->
      <span v-if="isExpired" class="flaticon-finish-line-optimized me-3">
        <span class="path1" />
        <span class="path2" />
        <span class="path3" />
        <span class="path4" />
        <span class="path5" />
        <span class="path6" />
        <span class="path7" />
        <span class="path8" />
        <span class="path9" />
        <span class="path10" />
      </span>

      <!-- Timer display -->
      <div class="text-center" id="active-pass-timer-time-in-pass">
        {{ isExpired ? totalTime : timeInPass }}
      </div>

      <!-- Check-in button for auto check-in -->
      <div
        v-if="showCheckInButton"
        class="auto-checkin text-green w-100 font-sm text-center"
        @click="$emit('show-check-in')"
      >
        {{ $t("common.check_in") }}
      </div>

      <!-- PIN attempt warning -->
      <div
        v-if="pinAttemptsCount >= 3"
        class="mt-2 text-danger comments-section-warning"
      >
        {{
          $t("common.this_student_is_flagged_for_3_unsuccessful_pin_attempts")
        }}
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "PassTimer"
}
</script>

<script setup>
import { computed } from "vue"

const props = defineProps({
  activePass: {
    type: Object,
    default: () => ({})
  },
  timeInPass: {
    type: String,
    default: ""
  },
  currentStatus: {
    type: String,
    default: ""
  },
  isPassLongRunning: {
    type: Boolean,
    default: false
  },
  pinAttemptsCount: {
    type: Number,
    default: 0
  },
  isActiveAutoCheckInModule: {
    type: Boolean,
    default: false
  }
})

// Define emits
const emit = defineEmits(["auto-approve", "auto-pause", "show-check-in"])

// Computed status flags
const isInQueue = computed(() => props.currentStatus === "queue")
const isWaiting = computed(() => props.currentStatus === "waiting")
const isApproved = computed(() => props.currentStatus === "approved")
const isExpired = computed(() => props.currentStatus === "expired")

// Computed classes based on status
const timerStatusClass = computed(() => {
  if (props.isPassLongRunning) {
    return "text-black"
  }
  if (isWaiting.value) {
    return "text-red"
  }
  if (isApproved.value) {
    return "text-green"
  }
  if (isExpired.value) {
    return "text-red"
  }
  if (isInQueue.value) {
    return "text-black"
  }
  return ""
})

// Get total time from pass
const totalTime = computed(() => {
  return props.activePass?.parent
    ? props.activePass?.parent?.total_time
    : props.activePass?.total_time
})

// Check if auto pass is valid
const isValidAutoPass = computed(() => {
  return (
    props.activePass?.flags &&
    props.activePass?.flags?.auto_pass &&
    props.activePass?.flags?.auto_pass?.is_valid
  )
})

// Show start button logic
const showStartButton = computed(() => {
  return (
    isWaiting.value &&
    isValidAutoPass.value &&
    (props.activePass?.flags?.auto_pass?.mode === "start_and_stop" ||
      props.activePass?.flags?.auto_pass?.mode === "start_only")
  )
})

// Show stop button logic
const showStopButton = computed(() => {
  if (isApproved.value) {
    if (
      props.activePass?.parent?.flags?.auto_pass?.is_valid &&
      (props.activePass?.parent?.flags?.auto_pass?.mode === "start_and_stop" ||
        props.activePass?.parent?.flags?.auto_pass?.mode === "stop_only")
    ) {
      return true
    }

    return (
      !props.activePass?.parent &&
      props.activePass?.flags &&
      props.activePass?.flags?.auto_pass &&
      props.activePass?.flags?.auto_pass?.is_valid &&
      (props.activePass?.flags?.auto_pass?.mode === "start_and_stop" ||
        props.activePass?.flags?.auto_pass?.mode === "stop_only")
    )
  }
  return false
})

// Show check icon logic
const showCheckIcon = computed(() => {
  return (
    isApproved.value &&
    !props.activePass?.parent &&
    props.activePass?.flags &&
    props.activePass?.flags?.auto_pass &&
    !props.activePass?.flags?.auto_pass?.is_valid
  )
})

// Show check-in button logic
const showCheckInButton = computed(() => {
  return (
    isApproved.value &&
    !props.activePass?.parent &&
    props.activePass?.flags &&
    props.activePass?.flags?.auto_check_in &&
    props.activePass?.flags?.auto_check_in?.is_valid === true &&
    props.isActiveAutoCheckInModule
  )
})

// Show queue position
const activePassQueuePosition = computed(() => {
  return props?.activePass?.queue_number || 0
})

// Handle check icon click
const handleCheckAction = () => {
  if (showCheckInButton.value) {
    emit("show-check-in")
  }
}
</script>
